import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogComponent, AutocompleteComponent, Inputs } from '../../../../../../Components';
import debounce from 'lodash/debounce';
import { GetOpenTenantLeads, CreateActivity } from '../../../../../../Services';
import { showError, showSuccess, showWarn } from '../../../../../../Helper';
import { config } from '../../../../../../config';

const PriceNegotiationDialog = ({
  isOpen,
  onClose,
  unitData,
  currentUser,
}) => {
  const { t } = useTranslation('UnitsView');
  const translationPath = '';
  const parentTranslationPath = 'UnitsView';

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [leadData, setLeadData] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [proposedPrice, setProposedPrice] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [remarks, setRemarks] = useState('');

  const createdBy = currentUser?.fullName || 'N/A';
  const assignedTo = unitData?.rent_listing_agent?.name || null;
  const originalPrice = unitData?.rent_price_fees || null;
  const unitId = unitData?.id || null;
  const isUnitAvailable = unitData?.unitStatus?.value === "available"
  const activityName = 'Price Negotiation';

  const autoGeneratedRemarks = useCallback(() => {
    return `Dear ${assignedTo || 'Agent'},

I hope this message finds you well. I would like to propose a rental price adjustment for the following lead:

Lead ID: ${selectedLead?.leadId || 'N/A'}  
Lead Name: ${selectedLead?.leadName || 'N/A'}  
Unit ID: ${unitId || 'N/A'}  
Original Rental Price: ${originalPrice || 'N/A'}  
Proposed Rental Price: ${proposedPrice || 'N/A'}

Kindly review this request and let me know your feedback at your earliest convenience. This proposal is based on the client’s current budget and market considerations.

Best regards,  
${createdBy}`;
  }, [assignedTo, createdBy, selectedLead, originalPrice, proposedPrice, unitId]);

  useEffect(() => {
    if (isOpen) {
      setRemarks(autoGeneratedRemarks());
    }
  }, [isOpen, autoGeneratedRemarks]);



  useEffect(() => {
    if ( originalPrice!== null &&   +proposedPrice > +originalPrice) {
      showWarn(t(`${translationPath}proposed-price-not-lower`, 'Proposed price is not lower than original'));
    }
     else if ( originalPrice!== null && +proposedPrice === +originalPrice) {
          showWarn(
            t(
              `${translationPath}You-are-proposing-the-same-price-as-the-original.-Please-confirm-this-is-intentional`,
              'You are proposing the same price as the original. Please confirm this is intentional'
            )
          );
        } }, [proposedPrice, originalPrice, t, translationPath]);


  const fetchReferredLeads = useCallback(
    async (searchValue = '') => {
      setIsLoading(true);
      try {
        const response = await GetOpenTenantLeads({
          agentId: currentUser.userId,
          search: searchValue,
          pageIndex: 1,
          pageSize: 25,
        });
        const leads = (response && response.result) || [];
        setLeadData(leads);
        if (leads.length === 0) {
          showWarn(t(`${translationPath}no-available-leads`, 'No available leads found.'));
        }
      } catch (error) {
        console.error('Error fetching referred leads:', error);
        setLeadData([]);
        showError(t(`${translationPath}failed-to-fetch-leads`, 'Failed to fetch leads'));
      }
      setIsLoading(false);
    },
    [currentUser.userId, t, translationPath]
  );

  const debouncedFetchLeads = useCallback(
    debounce((value) => fetchReferredLeads(value), 300),
    [fetchReferredLeads]
  );

  const validateForm = useCallback(() => {
    if (!isUnitAvailable) {
      return t(`${translationPath}unit-not-available`, 'Unit is not available');
    }
    if (!assignedTo) {
      return t(`${translationPath}no-listing-agent-assigned`, 'No listing agent assigned to this unit');
    }
    if (!originalPrice) {
      return t(`${translationPath}rental-price-missing`, 'Rental price is missing for the selected unit');
    }
    if (!selectedLead) {
      return t(`${translationPath}please-select-lead-id`, 'Please select a Lead ID');
    }
    if (!proposedPrice || isNaN(proposedPrice) || proposedPrice <= 0) {
      return t(`${translationPath}please-enter-valid-proposed-price`, 'Please enter a valid proposed price');
    }
    return null;
  }, [isUnitAvailable, assignedTo, originalPrice, selectedLead, proposedPrice, t, translationPath]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedLead(null);
      setProposedPrice('');
      setSearchInput('');
      setLeadData([]);
      setRemarks('');
    } else {
      fetchReferredLeads();
    }
  }, [isOpen, fetchReferredLeads]);

  const handleLeadChange = (event, newValue) => {
    setSelectedLead(newValue);
  };

  const handleProposedPriceChange = (event) => {
    setProposedPrice(event.target.value);
  };

  const handleRemarksChange = (event) => {
    setRemarks(event.target.value);
  };

  const handleSearchInputChange = (event, newInputValue) => {
    setSearchInput(newInputValue);
    debouncedFetchLeads(newInputValue);
  };

  const handleSave = async (event) => {
    event.preventDefault();

    const errorMessage = validateForm();
    if (errorMessage) {
      showError(errorMessage);
      return;
    }


    setIsSaving(true);

    const activityData = {
      assignAgentId: unitData?.rent_listing_agent?.id || null,
      activityTypeId:  config.PriceNegotiation,
      unitId: unitData?.id || null,
      relatedUnitNumberId: unitData?.id || null,
      relatedLeadNumberId: selectedLead?.leadId || null,
      relatedContactNumberId: null,
      relatedPortfolioId: null,
      relatedWorkOrderId: null,
      relatedMaintenanceContractId: null,
      activityDate: new Date().toISOString(),
      activityEndDate: new Date().toISOString(),
      activityDuration: null,
      subject: activityName,
      comments: remarks, 
      isOpen: true,
      price: proposedPrice,
      activityReminders: [],
      fileId: null,
      fileName: null,
      relatedActivityToId: null,
      files: [],
    };

    try {
      const res = await CreateActivity(activityData);
      if (!(res && res.status && res.status !== 200)) {
        showSuccess(t(`${translationPath}activity-created-successfully`, 'Activity created successfully'));
        onClose();
      } else {
        showError(t(`${translationPath}activity-creation-failed`, 'Failed to create activity'));
      }
    } catch (error) {
      console.error('Error creating activity:', error);
      showError(t(`${translationPath}activity-creation-failed`, 'Failed to create activity'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <DialogComponent
      isOpen={isOpen}
      onCloseClicked={handleClose}
      onSaveClicked={handleSave}
      titleText={t(`${translationPath}price-negotiation`, 'Price Negotiation')}
      maxWidth="sm"
      dialogContent={
        <div className="dialog-content">
          {!isUnitAvailable && (
            <div className="error-message">
              {t(`${translationPath}unit-not-available`, 'This action is only available for available units')}
            </div>
          )}
          <div className="mb-3">
            <Inputs
              idRef="unitIdRef"
              labelValue={t(`${translationPath}unit-id`, 'Unit ID')}
              value={unitId || 'N/A'}
              isDisabled
            />
          </div>
          <div className="mb-3">
            <Inputs
              idRef="originalPriceRef"
              labelValue={t(`${translationPath}original-price`, 'Original Price')}
              value={originalPrice || 'N/A'}
              isDisabled
            />
          </div>
          <div className="mb-3">
            <Inputs
              idRef="proposedPriceRef"
              labelValue={t(`${translationPath}proposed-price`, 'Proposed Price')}
              value={proposedPrice}
              onInputChanged={handleProposedPriceChange}
              type="number"
              inputPlaceholder={t(`${translationPath}enter-proposed-price`, 'Enter proposed price')}
            />
          </div>
          <div className="mb-3">
            <AutocompleteComponent
              idRef="leadIdRef"
              labelValue={t(`${translationPath}lead-id`, 'Lead ID')}
              data={leadData}
              displayLabel={(option) => `( ${option.leadId} ) - ${option.leadName}`}
              chipsLabel={(option) => `( ${option.leadId} ) - ${option.leadName}`}
              multiple={false}
              selectedValues={selectedLead}
              onChange={handleLeadChange}
              onInputChange={handleSearchInputChange}
              inputPlaceholder={t(`${translationPath}search-leads`, 'Search Leads')}
              isLoading={isLoading}
              parentTranslationPath={parentTranslationPath}
              translationPath={translationPath}
            />
          </div>
          <div className="mb-3">
            <Inputs
              idRef="remarksRef"
              labelValue={t(`${translationPath}remarks`, 'Remarks')}
              value={remarks}
              onInputChanged={handleRemarksChange}
              multiline
              rows={6}
              inputPlaceholder={t(`${translationPath}edit-remarks`, 'Edit remarks')}
            />
          </div>
        </div>
      }
      parentTranslationPath={parentTranslationPath}
      translationPath={translationPath}
      saveClasses="btns theme-solid bg-primary w-100 mx-2 mb-2"
      onCancelClicked={handleClose}
      saveIsDisabled={isSaving}
    />
  );
};

export default PriceNegotiationDialog;